import React, { FunctionComponent } from 'react'
import { Text, Skeleton, useIsBreakpointLarge, SkeletonShapes, Panel, useIsCashTheme } from '@afterpaytouch/core'
import { Table } from '../DataTable'
import styles from './style.module.scss'
import clsx from 'clsx'

interface LoadingListPageProps {
  tableCellHeaders: Array<string | JSX.Element>
  tableRowNumber?: number
  tableCellSkeletonShapes?: SkeletonShapes[]
  tableCellWidth?: number[]
  testNameSpace?: string
}

export const LoadingListPage: FunctionComponent<LoadingListPageProps> = ({
  tableCellHeaders,
  tableRowNumber = 4,
  testNameSpace = '',
  tableCellSkeletonShapes = [],
  tableCellWidth = [],
}) => {
  const isCashTheme = useIsCashTheme()
  const isBpLargeUp = useIsBreakpointLarge()

  const renderLoadingPanelHeader = (): JSX.Element => {
    return (
      <div className={styles.PanelHeadSkeleton}>
        <Skeleton kind={isCashTheme ? 'Default' : 'Primary'} />
      </div>
    )
  }

  const renderDesktopLoadingPanelContent = (): JSX.Element => {
    return (
      <>
        <Table>
          <Table.Head>
            <Table.Row>
              {tableCellHeaders.map((tableHeading, i) => (
                <Table.HeaderCell key={`table-row-headercell-${i}`} scope={'col'}>
                  {typeof tableHeading === 'string' ? (
                    <Text bold={true} size={'S'} color={'Gray40'}>
                      {tableHeading}
                    </Text>
                  ) : (
                    tableHeading
                  )}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Head>
          <Table.Content>
            {[...new Array(tableRowNumber)].map((item, index) => (
              <React.Fragment key={`table-row-content-${index}`}>
                <Table.Row>
                  <Table.DataCell width={tableCellWidth[0]}>
                    <span className={clsx(styles.TableRowSkeletonDefault, tableCellSkeletonShapes[0] === 'Circle' && styles.circle, 'ml-8')}>
                      <Skeleton shape={tableCellSkeletonShapes[0] ?? 'Default'} />
                    </span>
                  </Table.DataCell>
                  <Table.DataCell colSpan={4} width={tableCellWidth[1]}>
                    <span className={styles.TableRowSkeletonDefault}>
                      <Skeleton shape={tableCellSkeletonShapes[1] ?? 'Default'} />
                    </span>
                  </Table.DataCell>
                  <Table.DataCell width={tableCellWidth[2]}>
                    <span className={clsx(styles.TableRowSkeletonDefault, 'mr-8')}>
                      <Skeleton shape={tableCellSkeletonShapes[2] ?? 'Default'} />
                    </span>
                  </Table.DataCell>
                </Table.Row>
              </React.Fragment>
            ))}
          </Table.Content>
        </Table>
      </>
    )
  }

  const renderMobileLoadingPanelContent = (): JSX.Element => {
    return (
      <Table>
        <Table.Content>
          {Array.from(Array(tableRowNumber)).map((item, index) => (
            <React.Fragment key={`mobile-table-row-content-${index}`}>
              <Table.Row>
                <Table.DataCell>
                  <span className={styles.TableMobileLongSkeleton}>
                    <Skeleton />
                  </span>
                </Table.DataCell>
              </Table.Row>
            </React.Fragment>
          ))}
        </Table.Content>
      </Table>
    )
  }

  return (
    <div data-testid={testNameSpace !== '' ? `${testNameSpace}-loading-list-page` : 'loading-list-page'}>
      <Panel>
        <Panel.Head>
          <div className={styles.PanelHeadSkeleton}>{renderLoadingPanelHeader()}</div>
        </Panel.Head>
        <Panel.Content bordered={true}>{isBpLargeUp ? renderDesktopLoadingPanelContent() : renderMobileLoadingPanelContent()}</Panel.Content>
      </Panel>
    </div>
  )
}
